import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';

import { SidebarBrandComponent } from './sidebar/components/sidebar-brand.component';
import { SidebarNavigationComponent } from './sidebar/components/sidebar-navigation.component';
import { SidebarUserPanelComponent } from './sidebar/components/sidebar-user-panel.component';
import { SIDEBAR_MENU_GROUPS } from './sidebar/sidebar-menu.config';
import {
  bindSidebarMenuRuntime,
  isSidebarRouteActive,
} from './sidebar/sidebar-menu.runtime';
import { SidebarMenuGroupView, SidebarMenuItemView } from './sidebar/sidebar.models';
import { resolveSidebarRoutePolicy } from './sidebar/sidebar-route-policy';
import { SidebarService } from './sidebar/sidebar.service';
import { SidebarAuthAdapterService } from './sidebar/services/sidebar-auth-adapter.service';
import { SidebarSessionService } from './sidebar/services/sidebar-session.service';
import { SidebarVersionService } from './sidebar/services/sidebar-version.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    SidebarBrandComponent,
    SidebarNavigationComponent,
    SidebarUserPanelComponent,
  ],
  templateUrl: './sidebar/sidebar.component.html',
  styleUrls: ['./sidebar/sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private sidebarService = inject(SidebarService);
  private sidebarSessionService = inject(SidebarSessionService);
  private sidebarVersionService = inject(SidebarVersionService);
  private sidebarAuthAdapterService = inject(SidebarAuthAdapterService);
  private destroy$ = new Subject<void>();

  readonly isCollapsed = this.sidebarService.isCollapsed;
  readonly isMobileViewport = this.sidebarService.isMobileViewport;
  readonly isMobileOverlayOpen = this.sidebarService.isMobileOverlayOpen;
  readonly sidebarRenderWidth = this.sidebarService.sidebarRenderWidth;
  readonly isCollapsedForView = computed(() =>
    this.isMobileViewport() ? false : this.isCollapsed()
  );

  readonly username = this.sidebarSessionService.username;
  readonly userRole = this.sidebarSessionService.userRole;
  readonly userInitials = this.sidebarSessionService.userInitials;
  readonly backendVersion = this.sidebarVersionService.version;
  readonly backendVersionLoading = this.sidebarVersionService.isLoading;
  readonly backendVersionError = this.sidebarVersionService.errorMessage;
  readonly logoutInProgress = this.sidebarAuthAdapterService.isLoggingOut;
  readonly logoutError = this.sidebarAuthAdapterService.errorMessage;

  private currentRoute = signal('');
  private pendingRoute = signal<string | null>(null);

  readonly menuGroups = computed<SidebarMenuGroupView[]>(() =>
    bindSidebarMenuRuntime(SIDEBAR_MENU_GROUPS, this.pendingRoute() ?? this.currentRoute())
  );

  ngOnInit(): void {
    this.sidebarSessionService.loadSession();
    void this.sidebarVersionService.loadVersion();

    this.currentRoute.set(this.router.url);
    this.applyRoutePolicy(this.router.url);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const nextUrl = event.urlAfterRedirects || event.url;
        this.pendingRoute.set(null);
        this.currentRoute.set(nextUrl);
        this.sidebarService.closeMobile();
        this.applyRoutePolicy(nextUrl);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  closeMobileSidebar(): void {
    this.sidebarService.closeMobile();
  }

  goToHome(): void {
    this.navigateTo('/home');
  }

  onMenuItemSelected(item: SidebarMenuItemView): void {
    if (item.disabled || !item.route || this.isCurrentItem(item)) {
      return;
    }

    this.navigateTo(item.route);
  }

  logout(): void {
    void this.sidebarAuthAdapterService.logout();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isMobileOverlayOpen()) {
      this.sidebarService.closeMobile();
    }
  }

  private applyRoutePolicy(url: string): void {
    const policy = resolveSidebarRoutePolicy(url);
    if (policy === 'expand') {
      this.sidebarService.expand();
      return;
    }
    if (policy === 'collapse') {
      this.sidebarService.collapse();
    }
  }

  private navigateTo(route: string): void {
    this.sidebarService.closeMobile();
    this.pendingRoute.set(route);
    this.router
      .navigateByUrl(route)
      .then((success) => {
        if (!success) {
          this.pendingRoute.set(null);
        }
      })
      .catch(() => {
        this.pendingRoute.set(null);
      });
  }

  private isCurrentItem(item: SidebarMenuItemView): boolean {
    if (!item.route) {
      return false;
    }

    return isSidebarRouteActive(
      this.pendingRoute() ?? this.currentRoute(),
      item.route,
      'exact'
    );
  }
}
